<template>
  <div>
    <div class="upload-container">
      <div class="upload-section upload-files">
        <button
          type="button"
          class="upload-area"
          :class="{ 'drag-over': isDraggingOverFiles }"
          @click="openFilePicker"
          @drop="onFilesDropped"
          @dragover.prevent
          @dragenter="onDragEnterFiles"
          @dragleave="onDragLeaveFiles"
          aria-label="Upload files"
        >
          <input
            ref="fileInput"
            type="file"
            multiple
            @change="onFilesSelected"
            style="display: none"
            tabindex="-1"
            aria-hidden="true"
          />
          <section class="ex-center">
            <p>
              <NrIcon class="upload-icon" name="upload" iconType="solid" />
            </p>
            <p class="p-bottom-32">Upload files<br />(click or drop)</p>
          </section>
        </button>
      </div>
      <div class="upload-section upload-folders">
        <button
          type="button"
          class="upload-area"
          :class="{ 'drag-over': isDraggingOverFolders }"
          @click="openFolderPicker"
          @drop="onFilesDropped"
          @dragover.prevent
          @dragenter="onDragEnterFolders"
          @dragleave="onDragLeaveFolders"
          aria-label="Upload folders"
        >
          <input
            ref="folderInput"
            type="file"
            webkitdirectory
            multiple
            @change="onFilesSelected"
            style="display: none"
            tabindex="-1"
            aria-hidden="true"
          />
          <section class="ex-center">
            <p>
              <NrIcon class="upload-icon" name="folder" iconType="solid" />
            </p>
            <p class="p-bottom-32">Upload folders<br />(click or drop)</p>
          </section>
        </button>
      </div>
    </div>
    <NrMessage v-if="uploadLimitError" class="m-top-16">
      Too many files selected for upload. Select {{ uploadLimit }} or less
      files.
    </NrMessage>
    <div v-for="(file, index) in filesReadyForUpload" :key="index">
      <div class="m-bottom-8">
        <div class="p-bottom-4">
          <span>
            {{ file.name }}
          </span>
        </div>
        <NrProgress
          :id="file.name + file.lastModified"
          :value="0"
          show-value
          format="percent"
          >Queued</NrProgress
        >
      </div>
    </div>
  </div>
</template>

<script>
import isEmpty from 'lodash/isEmpty'
import uniqBy from 'lodash/uniqBy'
import NrIcon from '@nextrequestco/components/src/components/NrIcon'
import { v4 as uuidv4 } from 'uuid'
import NrMessage from '@nextrequestco/components/src/components/NrMessage'
import NrProgress from '@nextrequestco/components/src/components/NrProgress'

export default {
  components: { NrIcon, NrMessage, NrProgress },

  props: {
    uploadLimit: {
      type: Number,
      required: true
    },
    account: {
      type: Object
    },
    s3Bucket: {
      type: String
    },
    evaporate: {
      type: Object,
      default() {
        return {}
      }
    },
    requestId: {
      type: String
    }
  },

  data() {
    return {
      filesReadyForUpload: [], // List of files that are currently uploading or in the queued list
      uploadLimitError: false,
      isDraggingOverFiles: false,
      isDraggingOverFolders: false
    }
  },

  computed: {
    uploadLimitErrorMessage() {
      return `Too many files selected for upload. Select ${this.uploadLimit} or less files.`
    }
  },

  methods: {
    openFilePicker() {
      this.$refs.fileInput.click()
    },

    openFolderPicker() {
      this.$refs.folderInput.click()
    },

    onDragEnterFiles(event) {
      event.preventDefault()
      this.isDraggingOverFiles = true
    },

    onDragLeaveFiles(event) {
      event.preventDefault()
      const dropZone = event.currentTarget
      const relatedTarget = event.relatedTarget

      if (!dropZone.contains(relatedTarget)) {
        this.isDraggingOverFiles = false
      }
    },

    onDragEnterFolders(event) {
      event.preventDefault()
      this.isDraggingOverFolders = true
    },

    onDragLeaveFolders(event) {
      event.preventDefault()
      const dropZone = event.currentTarget
      const relatedTarget = event.relatedTarget

      if (!dropZone.contains(relatedTarget)) {
        this.isDraggingOverFolders = false
      }
    },

    onFilesSelected(event) {
      const files = Array.from(event.target.files)
      this.beginUploadingFiles(files)
      event.target.value = ''
    },

    getFile(entry) {
      return new Promise((resolve, reject) => entry.file(resolve, reject))
    },

    getEntry(dataTransferItem) {
      return dataTransferItem.webkitGetAsEntry
        ? dataTransferItem.webkitGetAsEntry()
        : dataTransferItem.getAsEntry()
    },

    // Returns a Promise which resolves with an array of all the files from the given directory, including in nested directories.
    filesFromDirectory(directoryEntry) {
      const directoryReader = directoryEntry.createReader()
      return new Promise((resolve) => {
        directoryReader.readEntries(async (entries) => {
          const fileEntries = []
          for (const entry of entries) {
            if (entry.isDirectory) {
              fileEntries.push(...(await this.filesFromDirectory(entry)))
            } else {
              fileEntries.push(await this.getFile(entry))
            }
          }
          resolve(fileEntries)
        })
      })
    },

    async onFilesDropped(event) {
      event.preventDefault()
      event.stopPropagation()

      this.isDraggingOverFiles = false
      this.isDraggingOverFolders = false

      const items = event.dataTransfer.items
      if (!items.length) return

      const promises = Array.from(items)
        .flatMap(this.getEntry)
        .map((entry) => {
          if (entry.isDirectory) {
            return this.filesFromDirectory(entry)
          } else {
            return this.getFile(entry)
          }
        })

      const files = await Promise.all(promises)
      this.beginUploadingFiles(files.flat())
    },

    uploadFiles(files) {
      if (this.uploadLimitReached(files)) {
        this.enableLimitErrorMsg(true)
        return
      } else if (!isEmpty(files)) {
        this.$emit('on-file-drop')
        this.enableLimitErrorMsg(false)
        this.filesReadyForUpload.push(...files)
      }

      if (isEmpty(this.evaporate) !== true) {
        let s3Folder
        if (this.requestId) {
          s3Folder = `${this.requestId}`
        } else {
          s3Folder = 'free_standing_or_uploaded_with_req_docs'
        }

        // Begin processing files
        if (files.length === 0) {
          return
        }

        const uploadPromises = files.flatMap((file) =>
          this.processFile(file, s3Folder)
        )

        Promise.all(uploadPromises)
          .then(() => {
            this.$emit('on-all-file-promises-resolved')
          })
          .catch((e) => {
            window?.Sentry?.captureException(e)
          })
      } else {
        const message = 'Evaporate file uploader not set.'
        window?.Sentry?.captureMessage(message)
      }
    },

    beginUploadingFiles(inputFiles) {
      const filesToUpload = this.selectFilesToUpload(inputFiles)
      this.uploadFiles(filesToUpload)
    },

    callBacks(file) {
      const self = this
      return {
        progress: (progressValue) => {
          self.updateProgressBar(file, 'progress', progressValue)
        },
        error: () => {
          self.updateProgressBar(file, 'error')
        },
        warn: () => {
          self.updateProgressBar(file, 'warn')
        },
        complete: (_xhr, awsKey) => {
          self.updateProgressBar(file, 'complete')
          const doc = self.setupDocumentObjectForPostgres(file, awsKey)
          this.$emit('on-file-upload-complete', doc)
        }
      }
    },

    enableLimitErrorMsg(val) {
      this.uploadLimitError = val
    },

    selectFilesToUpload(inputFiles) {
      const uniqueFiles = uniqBy(inputFiles, (file) => file.name)
      const validFiles = uniqueFiles.filter(
        (file) => file.size > 0 && file.name !== '.DS_Store'
      )

      // TODO: should we display an error on duplicate name?
      const alreadyUploadedNames = this.filesReadyForUpload.map((f) => f.name)
      const newFiles = validFiles.filter(
        (file) => !alreadyUploadedNames.includes(file.name)
      )

      return newFiles
    },

    processFile(file, s3Folder) {
      const fileType = file.name.split('.').pop()
      let name = ''
      if (isEmpty(this.account) === true) {
        return
      }
      if (fileType === 'pdf' && this.account.pdf_conversion_enabled) {
        name = `${
          this.account.subdomain
        }/${s3Folder}/original/${uuidv4()}.${fileType}`
      } else {
        name = `${this.account.subdomain}/${s3Folder}/${uuidv4()}.${fileType}`
      }
      const callbackMethods = this.callBacks(file)
      const promise = this.evaporate
        .add({
          name,
          file,
          complete: callbackMethods.complete,
          progress: callbackMethods.progress,
          error: callbackMethods.error,
          warn: callbackMethods.warn
        })
        .catch((e) => {
          window?.Sentry?.captureException(e)
        })
      return promise
    },

    setupDocumentObjectForPostgres(file, awsKey) {
      const docUploadUrl = `//${this.s3Bucket}.s3.amazonaws.com/${awsKey}`
      const docPath = docUploadUrl
      const docDesc = '' // TODO: Setup custom document description input field?
      const docName = file.name
      const docTitle = '' || docName // TODO: Setup custom document title input field?
      const docDate = '' // TODO: Setup custom document date?
      const fileName = docName
      return {
        description: docDesc,
        url: docPath,
        title: docTitle,
        filename: fileName,
        doc_date: docDate,
        link: false
      }
    },

    uploadLimitReached(files) {
      return files.length > this.uploadLimit
    },

    updateProgressBar(file, callBack, progressValue = '') {
      // callBack: Must be either 'progress', 'error' or 'complete'
      const progressBarWrapper = document.getElementById(
        `${file.name + file.lastModified}`
      )
      if (progressBarWrapper && progressBarWrapper.children) {
        const progressBarElement = progressBarWrapper.children[0]
        const progressValueElement = progressBarWrapper.children[1]

        if (callBack === 'progress' && !isNaN(progressValue)) {
          // Updates the progressbar for the file in the UI
          progressBarElement.value = Math.round(progressValue * 100)
          progressValueElement.innerText = `${progressBarElement.value}%`
          progressBarElement.classList.add('is-progress')
        } else if (callBack === 'complete') {
          // Sets success color
          progressBarElement.classList.add('is-complete')
        } else if (callBack === 'error') {
          // Sets the error message on the progress bar
          progressBarElement.classList.add('is-error')
          progressValueElement.innerText = 'Error uploading document try again'
        }
      }
    }
  }
}
</script>

<style lang="scss">
@import '@nextrequestco/components/src/styles/spacing.scss';
@import '@nextrequestco/components/src/styles/tokens/tokens.scss';

.upload-container {
  display: flex;
  gap: $spacing-16;
  width: 100%;

  .upload-section {
    flex: 1;

    .upload-area {
      cursor: pointer;
      width: 100%;
      padding: $spacing-4;
      border: $border-width-default dashed $color-gray-20;
      border-radius: $border-radius-default;
      position: relative;
      background: transparent;
      font-size: inherit;

      &:hover:not(.drag-over) {
        background-color: $color-gray-20;
        border: $border-width-default dashed $color-purple-110;
      }

      &.drag-over {
        background-color: $color-purple-10;
        border-color: $color-purple-110;
      }

      .ex-center {
        text-align: center;
        .upload-icon {
          font-size: $font-size-5;
          margin: $spacing-32 auto $spacing-32 auto;
        }
      }
    }
  }
}
</style>
